import { useMemo, useState } from "react";

import { useConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { useGetUser } from "@/users/v3";
import useGetUserClassrooms from "@/classrooms/hooks/useGetUserClassrooms";
import {
  ConceptNode,
  SubjectKey,
  SubjectToConceptData,
} from "../../Concepts.interface";

import {
  buildConceptTree,
  filterTreeBySearch,
  getDefaultTabId,
  selectNode,
  deselectNode,
  isNodeSelected,
} from "../utils";

const subjectTabMapping: { panelId: SubjectKey; name: string }[] = [
  {
    panelId: "ela",
    name: "ELA",
  },
  {
    panelId: "socialStudies",
    name: "Social Studies",
  },
  {
    panelId: "science",
    name: "Science",
  },
];

export const useConceptTaxonomyModal = ({
  setActive,
}: UseTaxonomyModalProps) => {
  const {
    getters: { taxonomyConcepts },
  } = useSearchPageContext();

  const { data: userData } = useGetUser();
  const { data: userClassrooms } = useGetUserClassrooms({
    userUniqueId: userData?.unique_id,
    enabled: !!userData?.unique_id,
  });
  const userSubjectSlugs = userClassrooms?.results.flatMap(
    (classroom) => classroom.subjects?.map((subject) => subject.slug) ?? [],
  );
  const defaultTabId = getDefaultTabId(userSubjectSlugs);

  const { handleConceptSubmit, draftSelectedIds, setDraftSelectedIds } =
    useConceptTaxonomyProvider();

  const [searchTerm, setSearchTerm] = useState<string>("");

  // Root subject tree contains tree for all subjects. Shaped like so:
  // { ela: ConceptNoda, socialStudies: ConceptNode, science: ConceptNode }
  const { rootNodeBySubject, nodeMap } = useMemo(() => {
    const tree = {} as SubjectToConceptData;
    let nodeMap = {} as Record<string, ConceptNode>;

    if (taxonomyConcepts) {
      subjectTabMapping.forEach((subject) => {
        const subjectData = taxonomyConcepts[subject.panelId];
        if (!subjectData?.items) return;

        const { rootNode, map } = buildConceptTree(subjectData.items);

        if (rootNode) {
          tree[subject.panelId] = rootNode;
        }
        nodeMap = { ...nodeMap, ...map };
      });
    }

    return { rootNodeBySubject: tree, nodeMap };
  }, [JSON.stringify(taxonomyConcepts)]);

  const handleToggle = (id: string, checked: boolean) => {
    const newState = checked
      ? selectNode(id, draftSelectedIds, nodeMap)
      : deselectNode(id, draftSelectedIds, nodeMap);

    setDraftSelectedIds(newState);
  };

  const handleIsNodeChecked = (id: string) => {
    return isNodeSelected(id, draftSelectedIds, nodeMap);
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
  };

  const handleSubmit = () => {
    handleConceptSubmit();
    setActive(false);
  };

  const filteredTree = useMemo(() => {
    if (!searchTerm) return rootNodeBySubject;
    const newTree = filterTreeBySearch(rootNodeBySubject, searchTerm);
    return newTree;
  }, [JSON.stringify(rootNodeBySubject), searchTerm]);

  return {
    defaultTabId,
    draftSelectedIds,
    filteredTree,
    handleIsNodeChecked,
    handleSearch,
    handleSubmit,
    handleToggle,
    subjectTabMapping,
  };
};

interface UseTaxonomyModalProps {
  setActive: (active: boolean) => void;
}
